import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react';
import { login as loginApi, logout as logoutApi, me as meApi } from '../api/authApi';
import { setOnSesionExpirada, tokenStorage } from '../api/apiClient';
import {
  NEGOCIOS_INICIALES,
  PRODUCTOS_INICIALES,
  VECINOS_INICIALES,
  PEDIDOS_INICIALES,
  FIADOS_INICIALES,
  PAGOS_INICIALES,
  SESION_INICIAL,
  CONVERSACIONES_INICIALES,
  MENSAJES_INICIALES,
  BLOQUEOS_INICIALES,
} from '../mock/data';

const AppContext = createContext(null);

let ID_SEQ = 1000;
const nuevoId = (prefijo) => `${prefijo}-${(++ID_SEQ).toString().padStart(4, '0')}`;

export function AppProvider({ children }) {
  const [sesion, setSesion] = useState(SESION_INICIAL);

  // ── Sesión REAL del backend (paralela al mock de arriba, para las
  //     páginas conectadas: login real y panel admin) ─────────────────
  // sesionBackend: null | { userId, correo, nombre, entidadTipo, entidadId, roles }
  const [sesionBackend, setSesionBackend] = useState(null);
  const [cargandoSesion, setCargandoSesion] = useState(true);

  useEffect(() => {
    // Al montar: si hay token, intenta hidratar la sesión con /auth/me
    let cancelado = false;
    (async () => {
      if (!tokenStorage.exists()) { setCargandoSesion(false); return; }
      try {
        const perfil = await meApi();
        if (cancelado) return;
        setSesionBackend({
          userId:      null, // no lo devolvemos por seguridad
          correo:      perfil.correo,
          nombre:      perfil.nombre,
          entidadTipo: perfil.entidadTipo,
          entidadId:   perfil.entidadId,
          correoVerificado: perfil.correoVerificado,
          // Los roles no vienen en /me actualmente — los inferimos del entidadTipo
          roles: rolesDesdeEntidadTipo(perfil.entidadTipo),
        });
      } catch {
        tokenStorage.clear();
      } finally {
        if (!cancelado) setCargandoSesion(false);
      }
    })();
    return () => { cancelado = true; };
  }, []);

  useEffect(() => {
    // 401 global → cerrar sesión y forzar recarga a /login
    setOnSesionExpirada(() => {
      setSesionBackend(null);
      if (window.location.pathname !== '/login') window.location.href = '/login';
    });
  }, []);

  const loginBackend = useCallback(async (telefono, password) => {
    const res = await loginApi(telefono, password);
    setSesionBackend({
      userId:      null,
      correo:      res.correo,
      nombre:      res.nombre,
      entidadTipo: res.entidadTipo,
      entidadId:   res.entidadId,
      correoVerificado: res.correoVerificado,
      roles: rolesDesdeEntidadTipo(res.entidadTipo),
    });
    return res;
  }, []);

  const cerrarSesionBackend = useCallback(async () => {
    await logoutApi();
    setSesionBackend(null);
  }, []);

  const [negocios, setNegocios] = useState(NEGOCIOS_INICIALES);
  const [productos, setProductos] = useState(PRODUCTOS_INICIALES);
  const [vecinos, setVecinos] = useState(VECINOS_INICIALES);
  const [pedidos, setPedidos] = useState(PEDIDOS_INICIALES);
  const [fiados, setFiados] = useState(FIADOS_INICIALES);
  const [pagos, setPagos] = useState(PAGOS_INICIALES);

  // Mensajería
  const [conversaciones, setConversaciones] = useState(CONVERSACIONES_INICIALES);
  const [mensajes, setMensajes] = useState(MENSAJES_INICIALES);
  const [bloqueos, setBloqueos] = useState(BLOQUEOS_INICIALES);

  // Carrito MONO-NEGOCIO del veci. { negocioId: string | null, items: [{ productoId, nombre, precio, cantidad }] }
  // El veci solo puede tener pedido activo con un negocio a la vez.
  const [carrito, setCarrito] = useState({ negocioId: null, items: [] });

  // Toast global sencillo
  const [toast, setToast] = useState({ open: false, msg: '', severity: 'success' });
  const notificar = useCallback((msg, severity = 'success') => {
    setToast({ open: true, msg, severity });
  }, []);
  const cerrarToast = useCallback(() => setToast((t) => ({ ...t, open: false })), []);

  // ---- Sesión ----
  const entrarComoNegocio = useCallback((negocioId = 'n-01') => {
    setSesion({ rol: 'negocio', negocioIdActivo: negocioId, veciIdActivo: null });
  }, []);
  const entrarComoVeci = useCallback((veciId = 'v-01') => {
    setSesion({ rol: 'veci', negocioIdActivo: null, veciIdActivo: veciId });
  }, []);
  const cerrarSesion = useCallback(() => {
    setSesion({ ...SESION_INICIAL, rol: null });
    setCarrito({ negocioId: null, items: [] });
  }, []);

  // ---- Registro (piloto con inscripción) ----
  const registrarNegocio = useCallback((datos) => {
    const nuevo = {
      id: nuevoId('n'),
      nombre: datos.nombre,
      categoria: datos.categoria,
      ubicacion: datos.ubicacion || '',
      direccion: datos.direccion || '',
      telefono: datos.telefono || '',
      horario: datos.horario || 'Lun-Sáb 7:00 a.m. - 8:00 p.m.',
      correo: datos.correo || '',
      planId: datos.planId || 'plan-vecino',
      // en un backend real estos irían por otro lado; los dejo acá para el mock
      contrasena: datos.contrasena || '',
      fechaRegistro: new Date().toISOString(),
    };
    setNegocios((prev) => [nuevo, ...prev]);
    setSesion({ rol: 'negocio', negocioIdActivo: nuevo.id, veciIdActivo: null });
    notificar(`¡Bienvenido a HolaVeci, ${nuevo.nombre}!`);
    return nuevo;
  }, [notificar]);

  const registrarVeci = useCallback((datos) => {
    const nuevo = {
      id: nuevoId('v'),
      nombre: datos.nombre,
      telefono: datos.telefono,
      direccion: datos.direccion || '',
      apodo: datos.apodo || '',
      correo: datos.correo || '',
      contrasena: datos.contrasena || '',
      fechaRegistro: new Date().toISOString(),
    };
    setVecinos((prev) => [nuevo, ...prev]);
    setSesion({ rol: 'veci', negocioIdActivo: null, veciIdActivo: nuevo.id });
    notificar(`¡Hola veci ${nuevo.nombre.split(' ')[0]}, bienvenida a la app!`);
    return nuevo;
  }, [notificar]);

  // Inicia sesión buscando por correo + contraseña en negocios y vecinos.
  // Devuelve { ok, rol, entidad } | { ok: false, error }
  const iniciarSesion = useCallback((correo, contrasena) => {
    const correoNorm = (correo || '').trim().toLowerCase();
    const negocio = negocios.find(
      (n) => (n.correo || '').toLowerCase() === correoNorm && n.contrasena === contrasena,
    );
    if (negocio) {
      setSesion({ rol: 'negocio', negocioIdActivo: negocio.id, veciIdActivo: null });
      notificar(`Bienvenido de nuevo, ${negocio.nombre}.`);
      return { ok: true, rol: 'negocio', entidad: negocio };
    }
    const veci = vecinos.find(
      (v) => (v.correo || '').toLowerCase() === correoNorm && v.contrasena === contrasena,
    );
    if (veci) {
      setSesion({ rol: 'veci', negocioIdActivo: null, veciIdActivo: veci.id });
      notificar(`Hola ${veci.nombre.split(' ')[0]}.`);
      return { ok: true, rol: 'veci', entidad: veci };
    }
    return { ok: false, error: 'Correo o contraseña incorrectos.' };
  }, [negocios, vecinos, notificar]);

  // ---- Catálogo (negocio) ----
  const productosDeNegocio = useCallback(
    (negocioId) => productos.filter((p) => p.negocioId === negocioId),
    [productos],
  );
  const crearProducto = useCallback((negocioId, { nombre, precio, unidad = 'un', codigoBarras = '' }) => {
    const nuevo = {
      id: nuevoId('p'),
      negocioId,
      nombre,
      precio: Number(precio),
      unidad,
      codigoBarras: codigoBarras.trim(),
      disponible: true,
    };
    setProductos((prev) => [nuevo, ...prev]);
    notificar('Producto agregado al catálogo.');
    return nuevo;
  }, [notificar]);
  const actualizarProducto = useCallback((productoId, cambios) => {
    setProductos((prev) => prev.map((p) => (p.id === productoId ? { ...p, ...cambios } : p)));
  }, []);
  const eliminarProducto = useCallback((productoId) => {
    setProductos((prev) => prev.filter((p) => p.id !== productoId));
    notificar('Producto eliminado.', 'info');
  }, [notificar]);

  // ---- Vecinos ----
  const vecinoPorId = useCallback((id) => vecinos.find((v) => v.id === id), [vecinos]);
  const negocioPorId = useCallback((id) => negocios.find((n) => n.id === id), [negocios]);
  const productoPorId = useCallback((id) => productos.find((p) => p.id === id), [productos]);
  const productoPorCodigoBarras = useCallback(
    (negocioId, codigo) => {
      const codigoNorm = (codigo || '').trim();
      if (!codigoNorm) return null;
      return productos.find(
        (p) => p.negocioId === negocioId && (p.codigoBarras || '').trim() === codigoNorm,
      ) || null;
    },
    [productos],
  );

  const crearVecino = useCallback(({ nombre, telefono, direccion = '', apodo = '' }) => {
    const existente = vecinos.find((v) => v.telefono === telefono);
    if (existente) return existente;
    const nuevo = { id: nuevoId('v'), nombre, telefono, direccion, apodo };
    setVecinos((prev) => [nuevo, ...prev]);
    return nuevo;
  }, [vecinos]);

  // ---- Pedidos ----
  const pedidosDeNegocio = useCallback(
    (negocioId) => pedidos.filter((p) => p.negocioId === negocioId).sort(porFechaDesc),
    [pedidos],
  );
  const pedidosDeVeci = useCallback(
    (veciId) => pedidos.filter((p) => p.veciId === veciId).sort(porFechaDesc),
    [pedidos],
  );

  const cambiarEstadoPedido = useCallback((pedidoId, nuevoEstado) => {
    setPedidos((prev) => prev.map((p) => (p.id === pedidoId ? { ...p, estado: nuevoEstado } : p)));
    const etiquetas = { nuevo: 'nuevo', preparacion: 'en preparación', entregado: 'entregado' };
    notificar(`Pedido marcado como ${etiquetas[nuevoEstado] || nuevoEstado}.`);
  }, [notificar]);

  const registrarFiadoParaPedido = useCallback((pedidoId) => {
    const pedido = pedidos.find((p) => p.id === pedidoId);
    if (!pedido) return;
    if (fiados.some((f) => f.pedidoId === pedidoId)) {
      notificar('Este pedido ya está registrado como fiado.', 'info');
      return;
    }
    const nuevoFiado = {
      id: nuevoId('fi'),
      veciId: pedido.veciId,
      negocioId: pedido.negocioId,
      pedidoId: pedido.id,
      monto: pedido.total,
      fecha: new Date().toISOString(),
      estado: 'pendiente',
    };
    setFiados((prev) => [nuevoFiado, ...prev]);
    setPedidos((prev) => prev.map((p) => (p.id === pedidoId ? { ...p, esFiado: true } : p)));
    notificar('Fiado registrado en la cuenta del veci.');
  }, [pedidos, fiados, notificar]);

  // ---- Fiados ----
  const fiadosDeNegocio = useCallback(
    (negocioId) => fiados.filter((f) => f.negocioId === negocioId).sort(porFechaDesc),
    [fiados],
  );
  const fiadosDeVeci = useCallback(
    (veciId) => fiados.filter((f) => f.veciId === veciId).sort(porFechaDesc),
    [fiados],
  );
  const fiadosPendientesEntre = useCallback(
    (negocioId, veciId) =>
      fiados.filter(
        (f) => f.negocioId === negocioId && f.veciId === veciId && f.estado === 'pendiente',
      ),
    [fiados],
  );
  const saldoFiadoVeciEnNegocio = useCallback(
    (negocioId, veciId) =>
      fiadosPendientesEntre(negocioId, veciId).reduce((acc, f) => acc + f.monto, 0),
    [fiadosPendientesEntre],
  );
  const saldoTotalPendienteNegocio = useCallback(
    (negocioId) =>
      fiados
        .filter((f) => f.negocioId === negocioId && f.estado === 'pendiente')
        .reduce((acc, f) => acc + f.monto, 0),
    [fiados],
  );

  const registrarAbono = useCallback((fiadoId, monto, medio = 'Efectivo') => {
    const fiado = fiados.find((f) => f.id === fiadoId);
    if (!fiado) return;
    const nuevoPago = {
      id: nuevoId('pg'),
      fiadoId,
      monto: Number(monto),
      medio,
      fecha: new Date().toISOString(),
    };
    setPagos((prev) => [nuevoPago, ...prev]);
    // Simplificación: cualquier abono >= monto marca el fiado como pagado.
    if (Number(monto) >= fiado.monto) {
      setFiados((prev) => prev.map((f) => (f.id === fiadoId ? { ...f, estado: 'pagado' } : f)));
      notificar('Fiado marcado como pagado.');
    } else {
      notificar('Abono registrado.');
    }
  }, [fiados, notificar]);

  // Registrar fiado directo desde el negocio (no viene de un pedido de la app).
  // datos: { veciId, monto, fecha, tipoRegistro, descripcion, items }
  const registrarFiadoManual = useCallback((negocioId, datos) => {
    const nuevo = {
      id: nuevoId('fi'),
      negocioId,
      veciId: datos.veciId,
      pedidoId: null,
      monto: Number(datos.monto),
      fecha: datos.fecha || new Date().toISOString(),
      estado: 'pendiente',
      tipoRegistro: datos.tipoRegistro || 'manual',
      // Cuando lo crea el negocio, queda pendiente de confirmación del veci
      estadoConfirmacion: 'pendiente_confirmacion',
      descripcion: datos.descripcion || '',
      items: datos.items || null,
    };
    setFiados((prev) => [nuevo, ...prev]);
    notificar('Fiado registrado. El veci lo verá para confirmar o reclamar.');
    return nuevo;
  }, [notificar]);

  // El veci confirma que el fiado es correcto.
  const confirmarFiado = useCallback((fiadoId) => {
    setFiados((prev) => prev.map((f) => (
      f.id === fiadoId ? { ...f, estadoConfirmacion: 'confirmado' } : f
    )));
    notificar('Fiado confirmado. Aparecerá en tu saldo con el negocio.');
  }, [notificar]);

  // El veci reclama que el fiado no coincide con lo que compró.
  const reclamarFiado = useCallback((fiadoId, motivo = '') => {
    setFiados((prev) => prev.map((f) => (
      f.id === fiadoId ? { ...f, estadoConfirmacion: 'reclamado', motivoReclamo: motivo } : f
    )));
    notificar('Reclamo enviado. El negocio lo verá y podrá corregirlo.', 'warning');
  }, [notificar]);

  // MOCK del OCR de Document AI. En backend real esto será:
  //   POST /api/fiados/analizar-recibo (multipart/form-data)
  //   → llama a Document AI (Expense/Invoice Processor)
  //   → devuelve { total, fecha, items: [{descripcion, cantidad, precioUnitario, subtotal}] }
  const analizarReciboMock = useCallback((archivo, negocioIdActivo) => {
    return new Promise((resolve) => {
      // Simula el tiempo de OCR real
      setTimeout(() => {
        const catalogo = productos.filter((p) => p.negocioId === negocioIdActivo);
        // Toma 2-4 productos aleatorios del catálogo del negocio
        const cuantos = Math.min(catalogo.length, 2 + Math.floor(Math.random() * 3));
        const seleccion = [...catalogo]
          .sort(() => Math.random() - 0.5)
          .slice(0, cuantos)
          .map((p) => ({
            nombre: p.nombre,
            cantidad: 1 + Math.floor(Math.random() * 3),
            precio: p.precio,
          }));
        const total = seleccion.reduce((s, it) => s + it.cantidad * it.precio, 0);
        resolve({
          total,
          fecha: new Date().toISOString(),
          items: seleccion,
          confianza: 0.94,
          origen: 'mock-document-ai',
        });
      }, 1800);
    });
  }, [productos]);

  const pagosDeFiado = useCallback(
    (fiadoId) => pagos.filter((p) => p.fiadoId === fiadoId).sort(porFechaDesc),
    [pagos],
  );

  // ---- Mensajería ----
  // Bloqueo unidireccional: solo el negocio puede bloquear al veci.
  const estaBloqueado = useCallback(
    (negocioId, veciId) => bloqueos.some((b) => b.negocioId === negocioId && b.veciId === veciId),
    [bloqueos],
  );

  const bloquearVeci = useCallback((negocioId, veciId, motivo = '') => {
    setBloqueos((prev) => {
      if (prev.some((b) => b.negocioId === negocioId && b.veciId === veciId)) return prev;
      return [...prev, { negocioId, veciId, motivo, fecha: new Date().toISOString() }];
    });
    notificar('Veci bloqueado. Ya no podrá enviarte mensajes.', 'info');
  }, [notificar]);

  const desbloquearVeci = useCallback((negocioId, veciId) => {
    setBloqueos((prev) => prev.filter((b) => !(b.negocioId === negocioId && b.veciId === veciId)));
    notificar('Veci desbloqueado.', 'success');
  }, [notificar]);

  // Encuentra o crea la conversación entre un veci y un negocio.
  const obtenerOCrearConversacion = useCallback((veciId, negocioId) => {
    const existente = conversaciones.find(
      (c) => c.veciId === veciId && c.negocioId === negocioId,
    );
    if (existente) return existente;
    const ahora = new Date().toISOString();
    const nueva = {
      id: nuevoId('conv'),
      veciId,
      negocioId,
      creada: ahora,
      actualizada: ahora,
      veciSalio: ahora,
      negocioSalio: ahora,
    };
    setConversaciones((prev) => [...prev, nueva]);
    return nueva;
  }, [conversaciones]);

  const conversacionEntre = useCallback(
    (veciId, negocioId) => conversaciones.find(
      (c) => c.veciId === veciId && c.negocioId === negocioId,
    ),
    [conversaciones],
  );

  const mensajesDeConversacion = useCallback(
    (conversacionId) => mensajes
      .filter((m) => m.conversacionId === conversacionId)
      .sort((a, b) => new Date(a.fecha) - new Date(b.fecha)),
    [mensajes],
  );

  // Enviar un mensaje. Devuelve el mensaje creado o { error } si está bloqueado.
  // datos: { texto, adjuntos?, tipo?, pedidoId? }
  const enviarMensaje = useCallback((veciId, negocioId, autor, datos) => {
    if (autor === 'veci' && estaBloqueado(negocioId, veciId)) {
      notificar('El negocio bloqueó tus mensajes.', 'error');
      return { error: 'bloqueado' };
    }
    const conv = obtenerOCrearConversacion(veciId, negocioId);
    const ahora = new Date().toISOString();
    const nuevoMensaje = {
      id: nuevoId('msg'),
      conversacionId: conv.id,
      autor, // 'veci' | 'negocio'
      tipo: datos.tipo || 'directo', // 'directo' | 'anuncio' | 'sistema'
      texto: datos.texto || '',
      adjuntos: datos.adjuntos || [],
      pedidoId: datos.pedidoId || null,
      fecha: ahora,
    };
    setMensajes((prev) => [...prev, nuevoMensaje]);
    setConversaciones((prev) => prev.map((c) => (
      c.id === conv.id
        ? { ...c, actualizada: ahora, ...(autor === 'veci' ? { veciSalio: ahora } : { negocioSalio: ahora }) }
        : c
    )));
    return nuevoMensaje;
  }, [estaBloqueado, obtenerOCrearConversacion, notificar]);

  // Marca la conversación como leída por el rol que la está viendo.
  const marcarConversacionLeida = useCallback((conversacionId, rol) => {
    const ahora = new Date().toISOString();
    setConversaciones((prev) => prev.map((c) => (
      c.id === conversacionId
        ? { ...c, ...(rol === 'veci' ? { veciSalio: ahora } : { negocioSalio: ahora }) }
        : c
    )));
  }, []);

  // Lista de conversaciones para un veci, ordenadas por actualización desc, con no-leídos calculados.
  const conversacionesDeVeci = useCallback((veciId) => {
    return conversaciones
      .filter((c) => c.veciId === veciId)
      .map((c) => {
        const msgs = mensajes.filter((m) => m.conversacionId === c.id);
        const ultimo = msgs.sort((a, b) => new Date(b.fecha) - new Date(a.fecha))[0] || null;
        const desde = new Date(c.veciSalio || 0).getTime();
        const noLeidosDirectos = msgs.filter(
          (m) => m.autor === 'negocio' && m.tipo === 'directo' && new Date(m.fecha).getTime() > desde,
        ).length;
        const noLeidosAnuncios = msgs.filter(
          (m) => m.autor === 'negocio' && m.tipo === 'anuncio' && new Date(m.fecha).getTime() > desde,
        ).length;
        return { ...c, ultimo, noLeidosDirectos, noLeidosAnuncios };
      })
      .sort((a, b) => new Date(b.actualizada) - new Date(a.actualizada));
  }, [conversaciones, mensajes]);

  // Lista de conversaciones para un negocio (excluye vecis bloqueados por defecto).
  const conversacionesDeNegocio = useCallback((negocioId, opciones = {}) => {
    const { incluirBloqueados = false } = opciones;
    return conversaciones
      .filter((c) => c.negocioId === negocioId)
      .filter((c) => incluirBloqueados || !estaBloqueado(negocioId, c.veciId))
      .map((c) => {
        const msgs = mensajes.filter((m) => m.conversacionId === c.id);
        const ultimo = msgs.sort((a, b) => new Date(b.fecha) - new Date(a.fecha))[0] || null;
        const desde = new Date(c.negocioSalio || 0).getTime();
        const noLeidos = msgs.filter(
          (m) => m.autor === 'veci' && new Date(m.fecha).getTime() > desde,
        ).length;
        return { ...c, ultimo, noLeidos, bloqueado: estaBloqueado(negocioId, c.veciId) };
      })
      .sort((a, b) => new Date(b.actualizada) - new Date(a.actualizada));
  }, [conversaciones, mensajes, estaBloqueado]);

  // Totales de no leídos (para badges del menú).
  const totalNoLeidosVeci = useCallback((veciId) => {
    const convs = conversacionesDeVeci(veciId);
    return {
      directos: convs.reduce((s, c) => s + c.noLeidosDirectos, 0),
      anuncios: convs.reduce((s, c) => s + c.noLeidosAnuncios, 0),
    };
  }, [conversacionesDeVeci]);

  const totalNoLeidosNegocio = useCallback((negocioId) => {
    return conversacionesDeNegocio(negocioId).reduce((s, c) => s + c.noLeidos, 0);
  }, [conversacionesDeNegocio]);

  // Envío masivo (campañas) del negocio a un conjunto de vecis.
  // Salta vecis bloqueados. Devuelve conteo enviado/omitido.
  const enviarCampana = useCallback((negocioId, veciIds, datos) => {
    let enviados = 0;
    let omitidos = 0;
    veciIds.forEach((veciId) => {
      if (estaBloqueado(negocioId, veciId)) {
        omitidos += 1;
        return;
      }
      enviarMensaje(veciId, negocioId, 'negocio', { ...datos, tipo: 'anuncio' });
      enviados += 1;
    });
    notificar(
      `Anuncio enviado a ${enviados} veci${enviados === 1 ? '' : 's'}${omitidos > 0 ? ` · ${omitidos} omitido${omitidos === 1 ? '' : 's'} por bloqueo` : ''}.`,
    );
    return { enviados, omitidos };
  }, [estaBloqueado, enviarMensaje, notificar]);

  // ---- Carrito (veci) ----
  // Estructura mono-negocio: el veci solo puede tener carrito con UN negocio a la vez.
  // agregarAlCarrito devuelve un resultado con estado:
  //   { ok: true } → agregado
  //   { ok: false, motivo: 'otro_negocio', negocioActualId, negocioActualNombre } → hay que confirmar cambio
  const agregarAlCarrito = useCallback((producto, cantidad = 1, { forzar = false } = {}) => {
    const negocioNuevo = producto.negocioId;
    let resultado = { ok: true };

    setCarrito((prev) => {
      // Si hay otro negocio activo y no se está forzando, no toca nada.
      if (prev.negocioId && prev.negocioId !== negocioNuevo && !forzar) {
        const nActual = negocios.find((n) => n.id === prev.negocioId);
        resultado = {
          ok: false,
          motivo: 'otro_negocio',
          negocioActualId: prev.negocioId,
          negocioActualNombre: nActual?.nombre || 'otro negocio',
        };
        return prev;
      }

      // Si se está forzando o el carrito está vacío, quedamos con el nuevo negocio.
      const baseItems = (prev.negocioId === negocioNuevo) ? prev.items : [];
      const idx = baseItems.findIndex((it) => it.productoId === producto.id);
      const nuevosItems = idx >= 0
        ? baseItems.map((it, i) => (i === idx ? { ...it, cantidad: it.cantidad + cantidad } : it))
        : [
            ...baseItems,
            {
              productoId: producto.id,
              nombre: producto.nombre,
              precio: producto.precio,
              cantidad,
            },
          ];
      return { negocioId: negocioNuevo, items: nuevosItems };
    });

    if (resultado.ok) notificar(`Agregado: ${producto.nombre}`);
    return resultado;
  }, [negocios, notificar]);

  const cambiarCantidadCarrito = useCallback((productoId, delta) => {
    setCarrito((prev) => {
      const items = prev.items
        .map((it) => (it.productoId === productoId ? { ...it, cantidad: it.cantidad + delta } : it))
        .filter((it) => it.cantidad > 0);
      if (items.length === 0) return { negocioId: null, items: [] };
      return { ...prev, items };
    });
  }, []);

  const quitarDelCarrito = useCallback((productoId) => {
    setCarrito((prev) => {
      const items = prev.items.filter((it) => it.productoId !== productoId);
      if (items.length === 0) return { negocioId: null, items: [] };
      return { ...prev, items };
    });
  }, []);

  const vaciarCarrito = useCallback(() => {
    setCarrito({ negocioId: null, items: [] });
  }, []);

  const totalCarrito = useCallback(
    () => carrito.items.reduce((s, it) => s + it.precio * it.cantidad, 0),
    [carrito],
  );

  const cantidadTotalCarrito = useCallback(
    () => carrito.items.reduce((s, it) => s + it.cantidad, 0),
    [carrito],
  );

  const enviarPedidoDeCarrito = useCallback(
    (nota = '') => {
      const veciId = sesion.veciIdActivo;
      const { negocioId, items } = carrito;
      if (!negocioId || items.length === 0 || !veciId) return null;
      const nuevo = {
        id: nuevoId('ped'),
        negocioId,
        veciId,
        items,
        total: items.reduce((s, it) => s + it.precio * it.cantidad, 0),
        estado: 'nuevo',
        esFiado: false,
        fecha: new Date().toISOString(),
        nota,
      };
      setPedidos((prev) => [nuevo, ...prev]);
      vaciarCarrito();
      notificar('Pedido enviado al negocio.');
      return nuevo;
    },
    [carrito, sesion.veciIdActivo, vaciarCarrito, notificar],
  );

  const value = useMemo(
    () => ({
      // sesión (mock legacy)
      sesion,
      entrarComoNegocio,
      entrarComoVeci,
      cerrarSesion,
      // sesión REAL del backend (conectada)
      sesionBackend,
      cargandoSesion,
      loginBackend,
      cerrarSesionBackend,
      // colecciones
      negocios,
      productos,
      vecinos,
      pedidos,
      fiados,
      pagos,
      // lookups
      negocioPorId,
      productoPorId,
      productoPorCodigoBarras,
      vecinoPorId,
      productosDeNegocio,
      pedidosDeNegocio,
      pedidosDeVeci,
      fiadosDeNegocio,
      fiadosDeVeci,
      fiadosPendientesEntre,
      saldoFiadoVeciEnNegocio,
      saldoTotalPendienteNegocio,
      pagosDeFiado,
      // acciones
      iniciarSesion,
      registrarNegocio,
      registrarVeci,
      crearProducto,
      actualizarProducto,
      eliminarProducto,
      crearVecino,
      cambiarEstadoPedido,
      registrarFiadoParaPedido,
      registrarFiadoManual,
      confirmarFiado,
      reclamarFiado,
      analizarReciboMock,
      registrarAbono,
      // carrito
      carrito,
      agregarAlCarrito,
      cambiarCantidadCarrito,
      quitarDelCarrito,
      vaciarCarrito,
      totalCarrito,
      cantidadTotalCarrito,
      enviarPedidoDeCarrito,
      // mensajería
      conversaciones,
      mensajes,
      conversacionesDeVeci,
      conversacionesDeNegocio,
      conversacionEntre,
      mensajesDeConversacion,
      obtenerOCrearConversacion,
      enviarMensaje,
      enviarCampana,
      marcarConversacionLeida,
      estaBloqueado,
      bloquearVeci,
      desbloquearVeci,
      totalNoLeidosVeci,
      totalNoLeidosNegocio,
      // ui
      toast,
      notificar,
      cerrarToast,
    }),
    [
      sesion, entrarComoNegocio, entrarComoVeci, cerrarSesion,
      sesionBackend, cargandoSesion, loginBackend, cerrarSesionBackend,
      negocios, productos, vecinos, pedidos, fiados, pagos,
      negocioPorId, productoPorId, productoPorCodigoBarras, vecinoPorId,
      productosDeNegocio, pedidosDeNegocio, pedidosDeVeci,
      fiadosDeNegocio, fiadosDeVeci, fiadosPendientesEntre,
      saldoFiadoVeciEnNegocio, saldoTotalPendienteNegocio, pagosDeFiado,
      registrarNegocio, registrarVeci, iniciarSesion,
      crearProducto, actualizarProducto, eliminarProducto,
      crearVecino, cambiarEstadoPedido,
      registrarFiadoParaPedido, registrarFiadoManual,
      confirmarFiado, reclamarFiado, analizarReciboMock,
      registrarAbono,
      carrito, agregarAlCarrito, cambiarCantidadCarrito, quitarDelCarrito,
      vaciarCarrito, totalCarrito, cantidadTotalCarrito, enviarPedidoDeCarrito,
      conversaciones, mensajes,
      conversacionesDeVeci, conversacionesDeNegocio, conversacionEntre,
      mensajesDeConversacion, obtenerOCrearConversacion,
      enviarMensaje, enviarCampana, marcarConversacionLeida,
      estaBloqueado, bloquearVeci, desbloquearVeci,
      totalNoLeidosVeci, totalNoLeidosNegocio,
      toast, notificar, cerrarToast,
    ],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp debe usarse dentro de <AppProvider>');
  return ctx;
}

function porFechaDesc(a, b) {
  return new Date(b.fecha) - new Date(a.fecha);
}

/**
 * Infiere los roles a partir del EntidadTipo del backend.
 * El endpoint /auth/me no devuelve los roles explícitos, así que los
 * derivamos del EntidadTipo (uno-a-uno con el rol de Identity).
 */
function rolesDesdeEntidadTipo(entidadTipo) {
  switch (entidadTipo) {
    case 'SUPERADMIN':    return ['SUPERADMIN'];
    case 'INVERSIONISTA': return ['INVERSIONISTA'];
    case 'NEGOCIO':       return ['ADMIN_NEGOCIO'];
    case 'VECI':          return ['VECI'];
    case 'DOMICILIARIO':  return ['DOMICILIARIO'];
    case 'GUIA':          return ['GUIA'];
    default:              return [];
  }
}
